import { entryMapKey } from "@/lib/entry-key";
// Standalone host for ExpandableSectionLayout (session 88).
//
// ExpandableSectionLayout is a purely presentational component — all data, save,
// document, and AI-extraction plumbing is injected by its parent. GenericCategoryPage
// is that parent for normal sections; this wrapper is the equivalent host for ONE
// section rendered OUTSIDE the category page (currently the "Anything else" band of
// Where Is Everything, whose WAT section is hidden from the category surface).
//
// It owns its own slice of the data layer: section fields, the section's entries
// (entry map + local edits + save mutation), per-row documents (counts + list +
// upload/extract via useGenericRowUpload), the pending-extraction review flow, and the
// file-view modal — so the rendered rows look and behave identically to any other
// expandable section. Reuse this anywhere an expandable section must mount standalone;
// do NOT re-implement the prop wiring inline.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useSingleFlight } from "@/hooks/useSingleFlight";
import {
  ExpandableSectionLayout,
  type PanelDoc,
} from "@/components/ExpandableSectionLayout";
import { DocumentSummaryModal, type DocumentSummaryTarget } from "@/components/DocumentSummaryModal";
import { EXPANDABLE_LAYOUT_CONFIG } from "@shared/constants/section-layout-config";
import { FileViewModal, type FileViewModalFile } from "@/components/FileViewModal";
import { ConfirmExtractionModal, type ConfirmExtractionField } from "@/components/ConfirmExtractionModal";
import { PendingExtractionBanner } from "@/components/PendingExtractionBanner";
import { FanOutBanner, type FanOutSection } from "@/components/FanOutBanner";
import { FanOutReviewModal, type FanOutSelection } from "@/components/FanOutReviewModal";
import { useGenericRowUpload } from "@/hooks/useGenericRowUpload";
import { useExtractionJobs } from "@/hooks/useExtractionJobs";
import { useToast } from "@/hooks/use-toast";
import { subscribeUploadComplete } from "@/lib/upload-events";
import { NO_EXTRACTION_TOAST } from "@/lib/extraction-toasts";
import { isLegacyBlankRow, isRowHidden } from "@/lib/table-row-visibility";
import { CATEGORY_SLUGS } from "@shared/constants/categories";
import type { GenericField, GenericEntry, GenericDocument } from "@shared/schema";

interface PendingExtraction {
  id: string;
  documentName: string;
  fieldsFilled: number;
  fields: ConfirmExtractionField[];
  summaryText?: string;
}

export interface ExpandableSectionStandaloneProps {
  clientId: string;
  /** The section to render. Its slug must have an EXPANDABLE_LAYOUT_CONFIG entry. */
  sectionId: string;
  sectionSlug: string;
  sectionName: string;
  /** Needed so brand-new rows / first-field entries can be created. */
  categoryId: string;
  readOnly?: boolean;
  /**
   * Spec hint: does the section's template declare an empty `{}` starter row? When
   * false (the default), all-blank seeded scaffold rows are suppressed exactly as
   * GenericCategoryPage does. WAT declares no empty starter → false. */
  declaresEmptyStarter?: boolean;
  /** s229 (D-229.102 C2): the empty-section line for a viewer who cannot add (see ExpandableSectionLayout). */
  emptyNote?: string;
}

export function ExpandableSectionStandalone({
  clientId,
  sectionId,
  sectionSlug,
  sectionName,
  categoryId,
  readOnly = false,
  declaresEmptyStarter = false,
  emptyNote,
}: ExpandableSectionStandaloneProps) {
  const { toast } = useToast();
  const { uploadRowDocument } = useGenericRowUpload();
  const config = EXPANDABLE_LAYOUT_CONFIG[sectionSlug];

  // Keys for invalidation: this section's entries + the WIE aggregator (so the
  // auto-pulled view + dashboard stay fresh when a manual row changes).
  const ENTRIES_QK = ["/api/clients", clientId, "generic-entries", "section", sectionId] as const;
  const WIE_QK = ["/api/clients", clientId, CATEGORY_SLUGS.WHERE_IS_EVERYTHING] as const;
  const invalidateEntries = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ENTRIES_QK });
    queryClient.invalidateQueries({ queryKey: WIE_QK });
    // Keep the dashboard's all-entries query (NEXT UP card) fresh.
    queryClient.invalidateQueries({ queryKey: ["/api/clients", clientId, "generic-entries", "all"] });
    queryClient.invalidateQueries({ queryKey: ["/api/unified/progress", clientId] });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, sectionId]);

  // ── Fields ──────────────────────────────────────────────────────────────────
  const { data: rawFields = [] } = useQuery<GenericField[]>({
    queryKey: ["/api/generic-sections", sectionId, "fields"],
    queryFn: async () => {
      const res = await fetch(`/api/generic-sections/${sectionId}/fields`, { credentials: "include" });
      if (!res.ok) return [];
      return res.json();
    },
    enabled: !!sectionId,
  });
  const fields = useMemo(
    () => rawFields.filter(f => f.isActive !== false).sort((a, b) => a.sortOrder - b.sortOrder),
    [rawFields],
  );

  // ── Entries (scoped to this section) ─────────────────────────────────────────
  const { data: entries = [] } = useQuery<GenericEntry[]>({
    queryKey: ENTRIES_QK,
    queryFn: async () => {
      const res = await fetch(`/api/clients/${clientId}/generic-entries`, { credentials: "include" });
      if (!res.ok) return [];
      const all: GenericEntry[] = await res.json();
      return all.filter(e => e.sectionId === sectionId);
    },
    enabled: !!clientId && !!sectionId,
  });

  const entryMap = useMemo<Record<string, GenericEntry>>(() => {
    const map: Record<string, GenericEntry> = {};
    for (const e of entries) {
      if (!e.rowId) continue; // expandable rows are always row-scoped
      map[entryMapKey(e.fieldId, e.rowId)] = e;
    }
    return map;
  }, [entries]);

  // Unique rowIds in first-seen (sortOrder) order, with legacy all-blank seeded
  // scaffold rows suppressed (same predicate as GenericCategoryPage).
  const rowIds = useMemo<string[]>(() => {
    const order: string[] = [];
    const byRow = new Map<string, GenericEntry[]>();
    for (const e of entries) {
      if (!e.rowId) continue;
      let arr = byRow.get(e.rowId);
      if (!arr) { arr = []; byRow.set(e.rowId, arr); order.push(e.rowId); }
      arr.push(e);
    }
    // s106 (MIG-1 Issue 1): also suppress fully soft-hidden rows (locked built-ins exempt).
    return order.filter(rid => {
      const rowEntries = byRow.get(rid) ?? [];
      return !isLegacyBlankRow(rowEntries, declaresEmptyStarter) && !isRowHidden(rid, rowEntries);
    });
  }, [entries, declaresEmptyStarter]);

  const section = useMemo(
    () => ({ id: sectionId, slug: sectionSlug, name: sectionName, fields }),
    [sectionId, sectionSlug, sectionName, fields],
  );
  const firstFieldId = fields[0]?.id;

  // ── Local edits ───────────────────────────────────────────────────────────────
  const [localEdits, setLocalEdits] = useState<Record<string, string>>({});
  // RADIO-RACE-1 (s154 round 2): synchronous mirror of localEdits so a discrete control's same-tick
  // onCellChange→onCellBlur reads the FRESH value (a stale-closure read used to drop the save).
  const pendingEditsRef = useRef<Record<string, string>>({});
  const getCellValue = useCallback((fieldId: string, rowId: string): string => {
    const key = entryMapKey(fieldId, rowId);
    return localEdits[key] ?? entryMap[key]?.value ?? "";
  }, [localEdits, entryMap]);
  const onCellChange = useCallback((fieldId: string, rowId: string, value: string) => {
    if (readOnly) return;
    pendingEditsRef.current[entryMapKey(fieldId, rowId)] = value;
    setLocalEdits(prev => ({ ...prev, [entryMapKey(fieldId, rowId)]: value }));
  }, [readOnly]);

  const upsertMutation = useMutation({
    mutationFn: async ({ field, rowId, value }: { field: GenericField; rowId: string; value: string }) => {
      const res = await apiRequest("POST", `/api/clients/${clientId}/generic-entries`, {
        categoryId, sectionId, fieldId: field.id, rowId, value,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ message: "Save failed" }));
        throw new Error(err.message || "Save failed");
      }
      return res.json();
    },
    onSuccess: (_, { field, rowId }) => {
      delete pendingEditsRef.current[entryMapKey(field.id, rowId)]; // RADIO-RACE-1: keep the ref in step
      setLocalEdits(prev => { const n = { ...prev }; delete n[entryMapKey(field.id, rowId)]; return n; });
      invalidateEntries();
    },
    onError: (e: Error) => toast({ title: "Error saving", description: e.message, variant: "destructive" }),
  });

  const onCellBlur = useCallback((field: GenericField, rowId: string) => {
    if (readOnly) return;
    const key = entryMapKey(field.id, rowId);
    // RADIO-RACE-1: read the fresh ref value (a same-tick radio onChange→onBlur), then the state map.
    const val = pendingEditsRef.current[key] ?? localEdits[key];
    if (val === undefined) return;
    upsertMutation.mutate({ field, rowId, value: val });
  }, [readOnly, localEdits, upsertMutation]);

  const onSaveFieldValue = useCallback(
    (field: GenericField, rowId: string, value: string) => upsertMutation.mutateAsync({ field, rowId, value }),
    [upsertMutation],
  );

  // OTHER-SPECIFY-1 (s155, D-155.6): persist the "Other → tell us more" detail to specify_text via the
  // SAME single-writer generic-entries POST (value-less body — the server keeps the existing value).
  const specifyMutation = useMutation({
    mutationFn: async ({ field, rowId, specifyText }: { field: GenericField; rowId: string; specifyText: string }) => {
      const res = await apiRequest("POST", `/api/clients/${clientId}/generic-entries`, {
        categoryId, sectionId, fieldId: field.id, rowId, specifyText,
      });
      if (!res.ok) { const err = await res.json().catch(() => ({ message: "Save failed" })); throw new Error(err.message || "Save failed"); }
      return res.json();
    },
    onSuccess: () => invalidateEntries(),
    onError: (e: Error) => toast({ title: "Error saving", description: e.message, variant: "destructive" }),
  });
  const getCellSpecify = useCallback(
    (fieldId: string, rowId: string): string => entryMap[entryMapKey(fieldId, rowId)]?.specifyText ?? "",
    [entryMap],
  );
  const onSaveSpecify = useCallback(
    (field: GenericField, rowId: string, text: string) => { specifyMutation.mutate({ field, rowId, specifyText: text }); },
    [specifyMutation],
  );

  // ── Add / delete / hide ───────────────────────────────────────────────────────
  // SINGLE-FLIGHT-PRIMITIVE-1 (s170). The rowId below is minted INSIDE the mutationFn, so two
  // fires produce two genuinely-distinct rows — there is no server-side conflict target that could
  // collapse them. The lock is the only possible guard.
  const addRowFlight = useSingleFlight();

  const addRowMutation = useMutation({
    mutationFn: async () => {
      if (!firstFieldId) throw new Error("Section has no fields");
      const rowId = crypto.randomUUID();
      await apiRequest("POST", `/api/clients/${clientId}/generic-entries`, {
        categoryId, sectionId, fieldId: firstFieldId, rowId, value: "", isComplete: false,
      });
    },
    onSuccess: invalidateEntries,
    onError: (e: Error) => toast({ title: "Error adding row", description: e.message, variant: "destructive" }),
  });

  const deleteRowMutation = useMutation({
    mutationFn: async (rowId: string) => {
      await apiRequest("DELETE", `/api/clients/${clientId}/generic-rows/${rowId}?sectionId=${sectionId}`);
    },
    onSuccess: () => { invalidateEntries(); toast({ title: "Row deleted" }); },
    onError: (e: Error) => toast({ title: "Error deleting row", description: e.message, variant: "destructive" }),
  });

  // "Hide from your view" is session-only (matches GenericCategoryPage's hiddenTableRows).
  const [hiddenRows, setHiddenRows] = useState<Set<string>>(new Set());
  const onHideRow = useCallback((rowId: string) => {
    setHiddenRows(prev => { const n = new Set(prev); n.add(rowId); return n; });
  }, []);
  const visibleRowIds = useMemo(() => rowIds.filter(r => !hiddenRows.has(r)), [rowIds, hiddenRows]);

  // ── Documents ───────────────────────────────────────────────────────────────
  const entryIds = useMemo(() => entries.map(e => e.id), [entries]);
  const { data: documentCounts = {} } = useQuery<Record<string, number>>({
    queryKey: ["/api/generic-entries/documents/counts", entryIds],
    queryFn: async () => {
      if (entryIds.length === 0) return {};
      const res = await apiRequest("POST", "/api/generic-entries/documents/counts", { entryIds });
      return res.json();
    },
    enabled: entryIds.length > 0,
  });

  const [fieldDocuments, setFieldDocuments] = useState<Record<string, PanelDoc[]>>({});
  const docKeyFor = useCallback((rowId: string) => entryMapKey(firstFieldId, rowId), [firstFieldId]);

  // D-170.6 conversions. None takes its own lock: the entry create is the (client, field, row)
  // upsert, the two deletes are named-id no-ops on a second fire, confirm CONSUMES its pending
  // row server-side, and populate already runs inside `populateFlight` (class A-enclosed).
  const createEntryMutation = useMutation({
    mutationFn: async (body: Record<string, unknown>) =>
      await apiRequest("POST", `/api/clients/${clientId}/generic-entries`, body),
  });
  const deleteDocumentMutation = useMutation({
    mutationFn: async (docId: string) => await apiRequest("DELETE", `/api/generic-documents/${docId}`),
  });
  const populateSectionMutation = useMutation({
    mutationFn: async (v: { jobId: string; body: Record<string, unknown> }) =>
      await apiRequest("POST", `/api/extraction-jobs/${v.jobId}/populate-section`, v.body),
  });
  const discardPendingMutation = useMutation({
    mutationFn: async (pendingId: string) =>
      await apiRequest("DELETE", `/api/pending-extractions/${pendingId}`),
  });
  const confirmPendingMutation = useMutation({
    mutationFn: async (v: { pendingId: string; body: Record<string, unknown> }) =>
      await apiRequest("POST", `/api/pending-extractions/${v.pendingId}/confirm`, v.body),
  });

  const loadFieldDocuments = useCallback(async (entryId: string, key: string) => {
    if (!entryId) return;
    try {
      const res = await fetch(`/api/generic-entries/${entryId}/documents`, { credentials: "include" });
      if (res.ok) {
        const docs: GenericDocument[] = await res.json();
        setFieldDocuments(prev => ({
          ...prev,
          [key]: docs.map(d => ({ id: d.id, filename: d.filename, fileSize: d.fileSize, mimeType: d.mimeType, uploadedAt: d.uploadedAt as unknown as string, extractedMetadata: d.extractedMetadata as { summary?: string | null; documentType?: string | null } | null })),
        }));
      }
    } catch {
      /* best-effort */
    }
  }, []);

  const onEnsureRowDocs = useCallback(async (rowId: string) => {
    if (!firstFieldId) return;
    const key = docKeyFor(rowId);
    const existing = entryMap[key];
    if (existing) {
      loadFieldDocuments(existing.id, key);
    } else {
      try {
        const res = await createEntryMutation.mutateAsync({
          categoryId, sectionId, fieldId: firstFieldId, rowId,
        });
        if (res.ok) {
          const newEntry = await res.json();
          invalidateEntries();
          setFieldDocuments(prev => ({ ...prev, [key]: [] }));
          loadFieldDocuments(newEntry.id, key);
        }
      } catch {
        /* best-effort */
      }
    }
  }, [firstFieldId, docKeyFor, entryMap, clientId, categoryId, sectionId, loadFieldDocuments, invalidateEntries]);

  const getRowDocCount = useCallback((rowId: string): number => {
    const entry = entryMap[docKeyFor(rowId)];
    return entry ? (documentCounts[entry.id] ?? 0) : 0;
  }, [entryMap, docKeyFor, documentCounts]);

  const onDownloadDocument = useCallback(async (docId: string) => {
    try {
      const res = await fetch(`/api/generic-documents/${docId}/download`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to get download URL");
      const { url, downloadUrl } = await res.json();
      window.open(url || downloadUrl, "_blank");
    } catch {
      toast({ title: "Download failed", description: "Could not download the document.", variant: "destructive" });
    }
  }, [toast]);

  const onDeleteDocument = useCallback(async (docId: string, rowId: string) => {
    const key = docKeyFor(rowId);
    try {
      await deleteDocumentMutation.mutateAsync(docId);
      setFieldDocuments(prev => ({ ...prev, [key]: (prev[key] || []).filter(d => d.id !== docId) }));
      queryClient.invalidateQueries({ queryKey: ["/api/generic-entries/documents/counts"] });
    } catch (e: any) {
      toast({ title: "Couldn't remove document", description: e?.message ?? "Please try again.", variant: "destructive" });
    }
  }, [docKeyFor, toast]);

  const [viewFile, setViewFile] = useState<FileViewModalFile | null>(null);
  const [viewingSummaryDoc, setViewingSummaryDoc] = useState<DocumentSummaryTarget | null>(null);

  // ── Pending-extraction review flow ────────────────────────────────────────────
  const [pendingExtraction, setPendingExtraction] = useState<PendingExtraction | null>(null);
  const [confirmFields, setConfirmFields] = useState<ConfirmExtractionField[]>([]);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmingRowId, setConfirmingRowId] = useState<string | null>(null);
  const [savingConfirmed, setSavingConfirmed] = useState(false);
  const [revealRowId, setRevealRowId] = useState<string | null>(null);

  // ── Cross-section fan-out review (s105 DB-1) — mirrors GenericCategoryPage so a
  // standalone-mounted section surfaces (rather than silently drops) the data the
  // uploaded document produced for OTHER sections. Fan-out NEVER auto-saves. ──────
  const [fanout, setFanout] = useState<{ jobId: string; sections: FanOutSection[]; reuploadWarning?: boolean } | null>(null);
  const [fanoutDismissed, setFanoutDismissed] = useState(false);
  const [fanoutOpen, setFanoutOpen] = useState(false);
  const [fanoutPopulatedIds, setFanoutPopulatedIds] = useState<Set<string>>(new Set());
  const [fanoutPopulatingId, setFanoutPopulatingId] = useState<string | null>(null);

  // SINGLE-FLIGHT-PRIMITIVE-1 (s170): populate-section APPENDS fresh rowIds — a double-fire doubles
  // the records. `populateAllFanout` awaits sequentially, so the lock releases between sections.
  const populateFlight = useSingleFlight();

  const populateFanoutSection = useCallback(async (targetSectionId: string, rowIndexes?: number[]) => {
    if (!fanout) return;
    await populateFlight.run(async () => {
    setFanoutPopulatingId(targetSectionId);
    try {
      await populateSectionMutation.mutateAsync({
        jobId: fanout.jobId,
        body: rowIndexes ? { sectionId: targetSectionId, rowIndexes } : { sectionId: targetSectionId },
      });
      setFanoutPopulatedIds(prev => new Set(prev).add(targetSectionId));
      queryClient.invalidateQueries({ queryKey: ["/api/clients", clientId, "generic-entries"] });
      queryClient.invalidateQueries({ queryKey: ["/api/unified/progress", clientId] });
    } catch {
      toast({ title: "Couldn't populate that section.", description: "Please try again.", variant: "destructive" });
    } finally {
      setFanoutPopulatingId(null);
    }
    });
  }, [fanout, clientId, toast, populateFlight]);

  const populateAllFanout = useCallback(async (selections: FanOutSelection[]) => {
    if (!fanout) return;
    for (const sel of selections) {
      if (!fanoutPopulatedIds.has(sel.sectionId) && sel.rowIndexes.length > 0) {
        await populateFanoutSection(sel.sectionId, sel.rowIndexes);
      }
    }
  }, [fanout, fanoutPopulatedIds, populateFanoutSection]);

  // Open the pending-extraction review modal for a given row + staged extraction.
  // Shared by the async-job completion handler and the phone-upload SSE path.
  const openPendingReview = useCallback((rowId: string | null, pending: PendingExtraction) => {
    setPendingExtraction(pending);
    setConfirmFields(pending.fields ?? []);
    setConfirmingRowId(rowId);
    setConfirmOpen(true);
  }, []);

  // ── Async extraction (session 90): watch the queued job; on completion either
  // open the review modal (confirm-before-saving) or surface the terminal state. ──
  const handleExtractionComplete = useCallback(async (info: { rowId: string | null; hasPending: boolean; pendingId: string | null; filledCount: number; hasFanout: boolean; jobId: string }) => {
    // Cross-section fan-out (s105): additive — runs alongside the upload-section review.
    if (info.hasFanout && info.jobId) {
      try {
        const res = await apiRequest("GET", `/api/extraction-jobs/${info.jobId}`);
        const job = await res.json();
        const sections: FanOutSection[] = job?.result?.fanout ?? [];
        if (sections.length > 0) {
          setFanout({ jobId: info.jobId, sections, reuploadWarning: !!job?.result?.reuploadOfPopulated });
          setFanoutDismissed(false);
          setFanoutOpen(false);
          setFanoutPopulatedIds(new Set());
        }
      } catch {
        /* fan-out is best-effort; the upload-section review still proceeds */
      }
    }
    if (info.hasPending && info.pendingId) {
      try {
        const res = await apiRequest("GET", `/api/pending-extractions/${info.pendingId}`);
        const pending = await res.json();
        if (pending?.id) {
          openPendingReview(info.rowId, {
            id: pending.id,
            documentName: pending.documentName,
            fieldsFilled: pending.fieldsFilled,
            fields: pending.fields,
            summaryText: pending.summaryText,
          });
        }
      } catch {
        /* manual refresh still surfaces the banner */
      }
    } else if (info.filledCount === 0 && !info.hasFanout) {
      invalidateEntries();
      toast(NO_EXTRACTION_TOAST);
    } else if (info.filledCount > 0) {
      invalidateEntries();
      toast({ title: "Data extracted from your document." });
    }
  }, [openPendingReview, invalidateEntries, toast]);

  const handleExtractionFailed = useCallback(() => {
    toast({ title: "We couldn't extract data from this document.", description: "Try uploading again.", variant: "destructive" });
  }, [toast]);

  const { watch: watchExtraction, isProcessing: isExtracting } = useExtractionJobs({
    clientId,
    onComplete: handleExtractionComplete,
    onFailed: handleExtractionFailed,
  });

  // One file → attach AND enqueue async extraction (per-row upload). Mirrors
  // GenericCategoryPage's handleExpandableRowUpload: attach to R2, then watch the
  // background extraction job which surfaces the review modal on completion.
  const onUploadFile = useCallback(async (rowId: string, file: File) => {
    if (!firstFieldId) return;
    const key = docKeyFor(rowId);
    try {
      const uploaded = await uploadRowDocument({
        clientId,
        categoryId,
        section: { id: sectionId, fields: fields.map(f => ({ id: f.id, sectionId })) },
        rowId,
        file,
        existingEntry: entryMap[key] ?? null,
      });
      if (!uploaded) return; // s170: the lock rejected this as a duplicate drop
      const { entry, document: newDoc, job } = uploaded;
      setFieldDocuments(prev => ({
        ...prev,
        [key]: [...(prev[key] || []), { id: newDoc.id, filename: newDoc.filename, fileSize: newDoc.fileSize, mimeType: newDoc.mimeType, uploadedAt: newDoc.uploadedAt }],
      }));
      loadFieldDocuments(entry.id, key);
      queryClient.invalidateQueries({ queryKey: ["/api/generic-entries/documents/counts"] });

      if (job) {
        watchExtraction(job.jobId, { sectionId, rowId });
      } else {
        toast({ title: "Document attached", description: "Your file was attached to this record." });
      }
    } catch (e: any) {
      toast({ title: "Upload failed", description: e?.message ?? "Could not upload the file.", variant: "destructive" });
    }
  }, [firstFieldId, docKeyFor, uploadRowDocument, clientId, categoryId, sectionId, fields, entryMap, loadFieldDocuments, watchExtraction, toast]);

  const discardPending = useCallback(async () => {
    if (pendingExtraction) {
      await discardPendingMutation.mutateAsync(pendingExtraction.id).catch(() => {});
      setPendingExtraction(null);
    }
    setConfirmOpen(false);
    setConfirmingRowId(null);
  }, [pendingExtraction]);

  const confirmPending = useCallback(async (fieldsToWrite: ConfirmExtractionField[]) => {
    if (!pendingExtraction) return;
    setSavingConfirmed(true);
    try {
      // DB-45 1H: the modal returns only the fields to write (safe + opted-in overwrites).
      await confirmPendingMutation.mutateAsync({
        pendingId: pendingExtraction.id,
        body: { fields: fieldsToWrite, rowId: confirmingRowId ?? undefined },
      });
      setPendingExtraction(null);
      invalidateEntries();
      setConfirmOpen(false);
      setConfirmingRowId(null);
    } catch (e: any) {
      toast({ title: "Failed to save", description: e.message, variant: "destructive" });
    } finally {
      setSavingConfirmed(false);
    }
  }, [pendingExtraction, confirmingRowId, invalidateEntries, toast]);

  // ── Live phone-upload completion (SSE) ──────────────────────────────────────────
  useEffect(() => {
    const unsub = subscribeUploadComplete(async (detail) => {
      // Async desktop extraction events are handled by useExtractionJobs — ignore here.
      if (detail.type !== "upload-complete") return;
      if (detail.clientId !== clientId || detail.sectionId !== sectionId || !detail.rowId || !detail.entryId || !firstFieldId) return;
      loadFieldDocuments(detail.entryId, entryMapKey(firstFieldId, detail.rowId));
      queryClient.invalidateQueries({ queryKey: ["/api/generic-entries/documents/counts"] });
      setRevealRowId(detail.rowId);

      // DB-45: surface cross-section fan-out for phone uploads too (the desktop path
      // already did via handleExtractionComplete). The worker now sends jobId +
      // hasFanout on the upload-complete event; fetch and open the same review banner.
      if (detail.hasFanout && detail.jobId) {
        try {
          const res = await apiRequest("GET", `/api/extraction-jobs/${detail.jobId}`);
          const job = await res.json();
          const sections: FanOutSection[] = job?.result?.fanout ?? [];
          if (sections.length > 0) {
            setFanout({ jobId: detail.jobId, sections, reuploadWarning: !!job?.result?.reuploadOfPopulated });
            setFanoutDismissed(false);
            setFanoutOpen(false);
            setFanoutPopulatedIds(new Set());
          }
        } catch {
          /* fan-out is best-effort; the row review still proceeds */
        }
      }

      if (detail.hasPending && detail.pendingId) {
        try {
          const res = await apiRequest("GET", `/api/pending-extractions/${detail.pendingId}`);
          const pending = await res.json();
          if (pending?.id) {
            setPendingExtraction({
              id: pending.id,
              documentName: pending.documentName,
              fieldsFilled: pending.fieldsFilled,
              fields: pending.fields,
              summaryText: pending.summaryText,
            });
            setConfirmFields(pending.fields);
            setConfirmingRowId(detail.rowId);
            setConfirmOpen(true);
          }
        } catch {
          /* manual refresh still surfaces the banner */
        }
      } else if (detail.documentId) {
        toast(NO_EXTRACTION_TOAST);
      }
    });
    return unsub;
  }, [clientId, sectionId, firstFieldId, loadFieldDocuments, toast]);

  const extractingRowIds = useMemo(
    () => new Set(visibleRowIds.filter(rid => isExtracting(sectionId, rid))),
    [visibleRowIds, isExtracting, sectionId],
  );

  if (!config || fields.length === 0) return null;

  return (
    <>
      {pendingExtraction && (
        <PendingExtractionBanner
          pending={{ fieldsFilled: pendingExtraction.fieldsFilled, documentName: pendingExtraction.documentName }}
          onReview={() => setConfirmOpen(true)}
          onDiscard={discardPending}
        />
      )}

      <ExpandableSectionLayout
        section={section}
        config={config}
        rowIds={visibleRowIds}
        readOnly={readOnly}
        emptyNote={emptyNote}
        clientId={clientId}
        secureFileSetupAt={null}
        getCellValue={getCellValue}
        getCellIsSeeded={(fieldId, rowId) => entryMap[entryMapKey(fieldId, rowId)]?.isSeeded === true}
        onCellChange={onCellChange}
        onCellBlur={onCellBlur}
        onSaveFieldValue={onSaveFieldValue}
        getCellSpecify={readOnly ? undefined : getCellSpecify}
        onSaveSpecify={readOnly ? undefined : onSaveSpecify}
        onAddRow={() => addRowFlight.runQuiet(() => addRowMutation.mutateAsync())}
        addRowDisabled={addRowFlight.inFlight}
        onDeleteRow={(rowId) => deleteRowMutation.mutate(rowId)}
        onHideRow={onHideRow}
        onUploadFile={onUploadFile}
        extractingRowIds={extractingRowIds}
        getRowDocCount={getRowDocCount}
        documentsByKey={fieldDocuments}
        onEnsureRowDocs={onEnsureRowDocs}
        onDownloadDocument={onDownloadDocument}
        onViewFile={setViewFile}
        onViewSummary={(doc) => doc.extractedMetadata?.summary && setViewingSummaryDoc({ filename: doc.filename, uploadedAt: doc.uploadedAt, summary: doc.extractedMetadata.summary })}
        onDeleteDocument={onDeleteDocument}
        revealRowId={revealRowId}
        onRowRevealed={() => setRevealRowId(null)}
      />

      <FileViewModal file={viewFile} onClose={() => setViewFile(null)} onDownload={onDownloadDocument} />
      <DocumentSummaryModal doc={viewingSummaryDoc} onOpenChange={(open) => { if (!open) setViewingSummaryDoc(null); }} />

      {/* Cross-section fan-out (s105 DB-1): banner + per-section review modal. */}
      {fanout && !fanoutDismissed && !fanoutOpen && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-40 w-[min(640px,calc(100vw-2rem))] shadow-lg">
          <FanOutBanner
            sections={fanout.sections}
            populatedCount={fanoutPopulatedIds.size}
            allPopulated={fanout.sections.every((s) => fanoutPopulatedIds.has(s.sectionId))}
            onReview={() => setFanoutOpen(true)}
            onSkip={() => setFanoutDismissed(true)}
            onClose={() => setFanoutDismissed(true)}
          />
        </div>
      )}
      {fanout && (
        <FanOutReviewModal
          open={fanoutOpen}
          onOpenChange={setFanoutOpen}
          sections={fanout.sections}
          populatedSectionIds={fanoutPopulatedIds}
          populatingSectionId={fanoutPopulatingId}
          onPopulate={populateFanoutSection}
          onPopulateAll={populateAllFanout}
          reuploadWarning={fanout.reuploadWarning}
        />
      )}

      {confirmOpen && (
        <ConfirmExtractionModal
          open={true}
          onOpenChange={(open) => { if (!open) { setConfirmOpen(false); setConfirmingRowId(null); } }}
          fields={confirmFields}
          onFieldChange={(idx, val) => setConfirmFields(prev => { const arr = [...prev]; arr[idx] = { ...arr[idx], value: val }; return arr; })}
          saving={savingConfirmed}
          onDiscard={discardPending}
          onConfirm={confirmPending}
        />
      )}
    </>
  );
}
